import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import Turnstile from '@/components/Turnstile';
import { guardAuth, guardMessage, reportAuthFailure, reportAuthSuccess } from '@/lib/authGuard';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

const Login: React.FC = () => {
  const { t, path } = useI18n();
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // Turnstile token. Verified SERVER-SIDE by the auth-guard function before any
  // credential is checked — the widget alone is never treated as proof.
  const [captcha, setCaptcha] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError(t('auth.errEmail'));
    setBusy(true);

    // Brute-force / credential-stuffing gate: per-IP + per-email lockout and
    // server-side bot challenge.
    const verdict = await guardAuth('login', email, captcha);
    if (!verdict.ok) {
      setBusy(false);
      return setError(guardMessage(verdict, t));
    }

    const { error: err } = await signIn(email, password);
    if (err) {
      // Burn a strike; 5 failures in 15 minutes locks this email + IP out.
      await reportAuthFailure('login', email);
      setBusy(false);
      return setError(t('auth.errCreds'));
    }

    await reportAuthSuccess('login', email);
    setBusy(false);
    navigate(params.get('next') || path('/dashboard'));
  };

  const reset = async () => {
    setError('');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError(t('auth.errEmail'));
    const verdict = await guardAuth('reset', email, captcha);
    if (!verdict.ok) return setError(guardMessage(verdict, t));
    await resetPassword(email);
    setNote(t('auth.resetSent'));
  };

  return (
    <div className="mx-auto max-w-md px-4 py-14 sm:px-6">
      <Seo title={t('auth.loginTitle')} description={t('auth.loginSub')} noindex />
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <h1 className="text-2xl font-extrabold text-slate-900">{t('auth.loginTitle')}</h1>
        <p className="mt-1 text-sm text-slate-500">{t('auth.loginSub')}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className={labelCls}>{t('common.email')}</label>
            <input type="email" autoComplete="username" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('auth.password')}</label>
            <input type="password" autoComplete="current-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Turnstile action="login" onVerify={setCaptcha} label={t('auth.captchaLabel')} />
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {note && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{note}</p>}
          <button type="submit" disabled={busy} className="w-full rounded-xl bg-slate-900 px-4 py-3.5 text-base font-bold text-white hover:bg-slate-800 disabled:opacity-50">
            {busy ? t('common.loading') : t('nav.login')}
          </button>
        </form>
        <button onClick={reset} className="mt-4 w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-900">
          {t('auth.forgot')}
        </button>

        <p className="mt-5 text-center text-sm text-slate-500">
          {t('auth.noAccount')} <Link to={path('/register')} className="font-bold text-orange-600">{t('nav.signup')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
